import { useAuthContext } from '@mntn/auth-ui'
import { createGaryClient, type GaryClient } from '@mntn/gary-client'
import type React from 'react'
import { createContext, useContext, useMemo, useRef } from 'react'
import { config } from '@/config'
import { LoadingElement } from '../session/loading.tsx'
import type { AppSession } from '../session/session-provider.tsx'
import { useSessionLoader } from './use-session-loader.ts'

type SessionContextType = {
  isLoaded: boolean
  isSignedIn: boolean
  getToken: () => Promise<string | null>
  session: AppSession | null
}

const SessionContext = createContext<SessionContextType | undefined>(undefined)

export const DashboardSessionProvider = ({ children }: { children: React.ReactNode }) => {
  const authContext = useAuthContext()
  const { isLoaded: isAuthLoaded, isAuthenticated, getToken } = authContext

  const garyClientRef = useRef<GaryClient | undefined>(undefined)
  if (!garyClientRef.current) {
    garyClientRef.current = createGaryClient({
      uri: `${config.VITE_GARY_GATEWAY_URL}/graphql`,
      getToken,
    })
  }
  const garyClient = garyClientRef.current

  const { session, isLoaded: isSessionLoaded } = useSessionLoader({
    garyClient,
    isAuthenticated,
    isAuthLoaded,
  })

  const isLoaded = useMemo(() => isAuthLoaded && isSessionLoaded, [isAuthLoaded, isSessionLoaded])

  const isSignedIn = useMemo(() => Boolean(isAuthenticated && session), [isAuthenticated, session])

  if (!isLoaded) {
    return <LoadingElement />
  }

  const authValue: SessionContextType = {
    isLoaded,
    isSignedIn,
    session,
    getToken,
  }

  return <SessionContext.Provider value={authValue}>{children}</SessionContext.Provider>
}

export const useDashboardSession = () => {
  const sessionContext = useContext(SessionContext)
  if (!sessionContext) {
    throw new Error('Authorization unavailable')
  }
  return sessionContext
}
